import {
    SyncScope,
    SyncSession,
    WorkspaceSourceDetails,
    WorkspaceSourceGraph,
    plainToSyncSession,
    plainToWorkspaceSourceDetails,
    plainToWorkspaceSourceGraph,
} from "./SyncSessionModel"
import { PatchChange, PatchOp } from "@webapp/util/Api"
import { UAParser } from "ua-parser-js"
import { useSyncServiceApi } from "./SyncServiceApi"

// Backend ops common to both clients. Defaults to web (HTTP); syncapp registers a LocalSyncApi one.
export interface SyncSessionClient {
    getSessionAsync(sessionId: string): Promise<SyncSession>
    refreshSessionAsync(sessionId: string): Promise<SyncSession>
    destroySessionAsync(sessionId: string): Promise<void>
    setWorkspaceAsync(sessionId: string, workspaceId: string): Promise<SyncSession>
    loadSourceDetailsAsync(sessionId: string, scope: SyncScope): Promise<WorkspaceSourceDetails>
    loadLivingWindowGraphAsync(sessionId: string): Promise<WorkspaceSourceGraph>
    // Deliver the session's changes for a sync: web posts to the sync service; desktop writes a
    // changes file locally and uploads it to ResearchData (cancellable via signal).
    submitChangesAsync(sessionId: string, syncId: string, scope: SyncScope, signal?: AbortSignal): Promise<void>
    // Device identity stamped on a workspace at first sync, used to recognize the local device later.
    getClientDeviceInfo(): string
}

let registered: SyncSessionClient | undefined

// Must run before the SyncSessionStore is first used -- the store captures the client once at setup.
export function setSyncSessionClient(client: SyncSessionClient) {
    registered = client
}

export function useSyncSessionClient(): SyncSessionClient {
    return registered ?? createWebSyncSessionClient()
}

function createWebSyncSessionClient(): SyncSessionClient {
    const syncApi = useSyncServiceApi()
    return {
        getSessionAsync: async id => plainToSyncSession(await syncApi.getPlainAsync(`sessions/${id}`)),
        refreshSessionAsync: async id => plainToSyncSession(await syncApi.postPlainAsync(`sessions/${id}/refresh`)),
        destroySessionAsync: id => syncApi.deleteAsync(`sessions/${id}`),
        setWorkspaceAsync: async (id, workspaceId) =>
            plainToSyncSession(await syncApi.patchPlainAsync(`sessions/${id}`, [new PatchChange('/workspaceId', workspaceId, PatchOp.Replace)])),
        loadSourceDetailsAsync: async (id, scope) =>
            plainToWorkspaceSourceDetails(await syncApi.postPlainAsync(`sessions/${id}/sourcedetails`, scope)),
        loadLivingWindowGraphAsync: async id =>
            plainToWorkspaceSourceGraph(await syncApi.getPlainAsync(`sessions/${id}/livingwindow`)),
        // No signal: the web submit is a quick POST (postPlainAsync takes none); web's cancel point is the file upload.
        submitChangesAsync: async (id, syncId, scope) => {
            await syncApi.postPlainAsync(`sessions/${id}/submit`, scope, { syncId })
        },
        getClientDeviceInfo: () => {
            // A browser upload has no device name/model; only the OS tells the user which device it came from.
            const ua = new UAParser()
            return JSON.stringify({ os: ua.getOS().name })
        },
    }
}
